import { useState, useEffect, useCallback } from 'react';
import FilterGroup, { FilterOption } from './FilterGroup';
import { useCountries, useStates, useCities } from '../../api/hooks/location.hooks';
import type { LocationDto } from '../../api/hooks/location.hooks';

export interface LocationSelection {
  country?: string;
  state?: string;
  city?: string;
}

interface LocationFilterGroupProps {
  title?: string;
  initialValues?: LocationSelection;
  onChange: (selection: LocationSelection) => void;
}

const LocationFilterGroup = ({

  initialValues,
  onChange
}: LocationFilterGroupProps) => {
  // Selected values
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);

  // Fetch location data
  const { data: countriesResponse, isLoading: isLoadingCountries } = useCountries();
  const { data: statesResponse, isLoading: isLoadingStates } = useStates(selectedCountries[0]);
  const { data: citiesResponse, isLoading: isLoadingCities } = useCities(selectedStates[0]);

  // Convert response data to FilterOption format
  const countryOptions: FilterOption[] = (countriesResponse?.data || []).map((country: LocationDto) => ({
    id: country.id,
    name: country.name
  }));

  const stateOptions: FilterOption[] = (statesResponse?.data || []).map((state: LocationDto) => ({
    id: state.id,
    name: state.name,
    disabled: selectedCountries.length === 0
  }));

  const cityOptions: FilterOption[] = (citiesResponse?.data || []).map((city: LocationDto) => ({
    id: city.id,
    name: city.name,
    disabled: selectedStates.length === 0
  }));

  // Initialize from initial values
  useEffect(() => {
    if (initialValues) {
      if (initialValues.country) setSelectedCountries([initialValues.country]);
      if (initialValues.state) setSelectedStates([initialValues.state]);
      if (initialValues.city) setSelectedCities([initialValues.city]);
    }
  }, [initialValues]);

  // Handle changes
  const handleCountryChange = useCallback((selected: string[]) => {
    setSelectedCountries(selected);
    setSelectedStates([]);
    setSelectedCities([]);
    onChange({
      country: selected[0],
      state: undefined,
      city: undefined
    });
  }, [onChange]);

  const handleStateChange = useCallback((selected: string[]) => {
    setSelectedStates(selected);
    setSelectedCities([]);
    onChange({
      country: selectedCountries[0],
      state: selected[0],
      city: undefined
    });
  }, [onChange, selectedCountries]);

  const handleCityChange = useCallback((selected: string[]) => {
    setSelectedCities(selected);
    onChange({
      country: selectedCountries[0],
      state: selectedStates[0],
      city: selected[0]
    });
  }, [onChange, selectedCountries, selectedStates]);

  return (
    <div className="rounded-xl">
      <FilterGroup
        title="País"
        options={countryOptions}
        selectedValues={selectedCountries}
        onSelectionChange={handleCountryChange}
        isLoading={isLoadingCountries}
      />

      <FilterGroup
        title="Departamento"
        options={stateOptions}
        selectedValues={selectedStates}
        onSelectionChange={handleStateChange}
        isLoading={isLoadingStates}
        disabled={selectedCountries.length === 0}
      />

      <FilterGroup
        title="Municipio"
        options={cityOptions}
        selectedValues={selectedCities}
        onSelectionChange={handleCityChange}
        isLoading={isLoadingCities}
        disabled={selectedStates.length === 0}
      />
    </div>
  );
};

export default LocationFilterGroup; 
